import ApiServer from "../../ApiServer.ts";
import * as FwDatas from "../../FwDatas.ts";
import * as Html from "../../util/Html.ts";
import * as i18n from "../../i18n.ts";
import { Searcher } from "../searchController.ts";
import { TaskCategoryCache ,TaskListCache } from "../../server/objects/TaskBeans.ts";
import { parseLeadingQuantity } from "../../lib/ingredientParser.ts";
import { formatQuantityPrefix } from "../../lib/unitFormat.ts";

const SuggestionBuilder = {
    getPromise(listType){
      if(this[listType]) return this[listType];
      return (this[listType] = new Promise(function(resolve, reject) {
        ApiServer.taskgettasksuggestionswcat(listType,null,null, function(items){
          let familyId = FwDatas.getFamily().family_id;
          items.suggestions.forEach(sugg=>sugg.taskCategoryId = "taskCategory/"+familyId+"_"+sugg.categoryId);
          resolve(items.suggestions);
        },reject)
      }))
    }
}

type TaskSuggestionItem = {
  value:string
  taskCategoryId:string
}
class TaskSuggestionSet{
  data :TaskSuggestionItem[] = []
  keys :{[key:string]:TaskSuggestionItem} = {}
  constructor(){
  }
  push(... items :TaskSuggestionItem[]){
    for(let item of items){
      let key = (item.value+'|'+item.taskCategoryId);
      if(this.keys[key]) continue;
      this.data.push(item);
      this.keys[key]=item;
    }
  }
}
const datasource = {
    suggestions:function(metaId){
      return SuggestionBuilder.getPromise(FwDatas.get(metaId).taskListType);
    },
    sametype:function(metaId){
      const
        currentList = TaskListCache.get(metaId),
        currentType = currentList.taskListType,
        result = [];

      TaskListCache.datas.filter(list=>list.taskListType===currentType).forEach(list=>{
        list.ongoing.forEach(task=>result.push({value:task.text,taskCategoryId:task.taskCategoryId}));
        list.complete.forEach(task=>result.push({value:task.text,taskCategoryId:task.taskCategoryId}));
      })
      return result;
    }
}
function getCategory(categoryDef){
  if(categoryDef.categoryId){
    var match = TaskCategoryCache.datas.filter(cat=>cat.systemCategoryId===categoryDef.categoryId);
    if(match) return match[0];
  }else if(categoryDef.taskCategoryId){
    return FwDatas.get(categoryDef.taskCategoryId)
  }

}
function initDom(input: HTMLInputElement){
  input.setAttribute('autocomplete','off')

  const dataSourcesString = input.getAttribute('data-source')?.split(' ') || [];
  let allsuggestions: TaskSuggestionSet | undefined;
  let allsuggestionsPromises: Promise<void> | undefined;

  const form = input.closest('form');
  if(!form) return;

  //outside of shopping lists a leading number belongs to the item name, never to a quantity
  const quantityAware = TaskListCache.get(Html.getDomMetaId(input))?.taskListType === 'SHOPPING_LIST';

  /** The typed line split into its quantity head and the item name to search on. */
  function splitTyped(){
    const parsed = quantityAware ? parseLeadingQuantity(input.value,i18n.getShortLanguage()) : null;
    return parsed || {name:input.value,quantity:0,unit:''};
  }

  const formRect = form.getBoundingClientRect();
  const fontSize = window.getComputedStyle(input).fontSize;

  const theNode = document.createElement('div');
  theNode.className = 'selectable border-pane';
  theNode.style.cssText = `font-size:${fontSize};font-weight:400;line-height:200%;display:none;cursor:pointer;z-index:12;position:absolute;left:var(--space);width:${formRect.width}px;`;
  form.parentElement?.insertBefore(theNode, form.nextSibling);

  async function initializeInput(){
    if(allsuggestionsPromises) {
      await allsuggestionsPromises;
      return;
    }
    const metaId = Html.getDomMetaId(input);
    const arrayOfArray = await Promise.all(dataSourcesString.map(str=>datasource[str](metaId)))
    allsuggestions = new TaskSuggestionSet();
    arrayOfArray.forEach(array=>allsuggestions!.push(...array));
  }

  function display(){
    const text = splitTyped().name;
    let matchedSugg: TaskSuggestionItem[] = [];

    if(allsuggestions && text.trim().length>0){
      matchedSugg = new Searcher()
        .filterSuggestion(text.trim(), allsuggestions.data)
        .slice(0,5);
    }

    const paddingLeft = window.getComputedStyle(input).paddingLeft;
    theNode.innerHTML = matchedSugg.map(sug=>{
      const category = getCategory(sug);
      const json = Html.escape(JSON.stringify({value:sug.value,categoryId:category?category.metaId:undefined})).replace("'","&apos;");

      return `<flexrow data-json='${json}' style="padding-left:${paddingLeft}">
        <span style="flex:1;">${Html.escape(sug.value)}</span>
        <span style="flex:1;text-align:right;color:var(--fwTextSecondary)">${Html.escape(category?category.name:'')}</span>
        </flexrow>`
    }).join('')

    if(matchedSugg.length){
      theNode.style.display = '';
    }else{
      theNode.style.display = 'none';
    }
  }
  const handleInputEvents = async () => {
    await initializeInput()
    display()
  };

  ['focus', 'change', 'input', 'keypress', 'paste'].forEach(eventType => {
    input.addEventListener(eventType, handleInputEvents);
  });

  input.addEventListener('blur', function(){
    theNode.style.display = 'none';
  });

  function pick(div: HTMLElement){
    const jsonStr = div.getAttribute('data-json');
    if(!jsonStr) return;
    const json = JSON.parse(jsonStr);
    //a quantity typed or picked before choosing the suggestion has to survive it
    const {quantity,unit} = splitTyped();
    const prefix = formatQuantityPrefix(quantity,unit);
    input.value = prefix ? prefix+' '+json.value : json.value;
    const taskCategoryInput = form.querySelector('[name="taskCategoryId"]') as HTMLInputElement;
    if(taskCategoryInput) {
      taskCategoryInput.value = json.categoryId ? json.categoryId : '';
    }
  }

  input.addEventListener('keydown', function(event: KeyboardEvent){
    let down = event.keyCode === 40;
    let up = event.keyCode === 38;

    const flexrows = theNode.querySelectorAll('flexrow');
    if(event.keyCode === 9 && flexrows.length){
      event.preventDefault();
      if(event.shiftKey){
        up = true
      }else{
        down = true;
      }
    }

    if(down){
      const selected = theNode.querySelector('.selected');
      if(selected){
        selected.classList.remove('selected');
        const next = selected.nextElementSibling as HTMLElement;
        if(next){
          next.classList.add('selected');
        }else{
          flexrows[0]?.classList.add('selected');
        }
      }else{
        flexrows[0]?.classList.add('selected');
      }
      return;
    }

    if(up){
      const selected = theNode.querySelector('.selected');
      if(selected){
        selected.classList.remove('selected');
        const prev = selected.previousElementSibling as HTMLElement;
        if(prev){
          prev.classList.add('selected');
        }else{
          flexrows[flexrows.length - 1]?.classList.add('selected');
        }
      }else{
        flexrows[flexrows.length - 1]?.classList.add('selected');
      }
      return;
    }

    if(event.keyCode === 10 || event.keyCode === 13){
      const selected = theNode.querySelector('.selected') as HTMLElement;
      if(selected){
        pick(selected);
      }else{
        const firstSpan = theNode.querySelector('span');
        if(firstSpan && splitTyped().name === firstSpan.textContent){
          const firstFlexrow = theNode.querySelector('flexrow') as HTMLElement;
          if(firstFlexrow) pick(firstFlexrow);
        }
      }
    }
  });

  theNode.addEventListener('mousedown', Html.createDelegateListener('flexrow', function(evt, target){
    pick(target);
    form.requestSubmit();
  }));
}

export {initDom}
Html.registerWidget('widget/form/autocomplete',{initDom})
